/**
 * Static rectangular platform. Optional rotation (degrees) for angled
 * surfaces that deflect bounces in fun directions. Optional `bouncy` raises
 * restitution so the deflection is snappy.
 */

import Matter from 'matter-js';
import { Graphics } from 'pixi.js';

export interface PlatformOptions {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Rotation in degrees, around the rect's center. Defaults to 0. */
  angle?: number;
  /** Bouncier surface for trick deflections. Defaults to false. */
  bouncy?: boolean;
}

export class Platform {
  readonly body: Matter.Body;
  readonly view: Graphics;

  constructor(opts: PlatformOptions) {
    const cx = opts.x + opts.w / 2;
    const cy = opts.y + opts.h / 2;
    const angleRad = ((opts.angle ?? 0) * Math.PI) / 180;
    const restitution = opts.bouncy ? 0.85 : 0.35;
    this.body = Matter.Bodies.rectangle(cx, cy, opts.w, opts.h, {
      isStatic: true,
      friction: 0.05,
      frictionStatic: 0.05,
      restitution,
      angle: angleRad,
      label: 'platform',
    });

    // Draw centered, then rotate/translate the Graphics object to match.
    const g = new Graphics();
    const fill = opts.bouncy ? 0x4a88ff : 0x111119;
    const stroke = opts.bouncy
      ? { color: 0xffffff, width: 2, alpha: 0.7 }
      : { color: 0xffffff, width: 2, alpha: 0.35 };
    g.roundRect(-opts.w / 2, -opts.h / 2, opts.w, opts.h, Math.min(opts.h / 2, 12))
      .fill(fill)
      .stroke(stroke);
    g.x = cx;
    g.y = cy;
    g.rotation = angleRad;
    this.view = g;
  }
}
